/**
 * map-tilt.ts — pure camera-tilt math for the 3D live tracking view (ADR-0033).
 *
 * CONTRACT
 * --------
 * Framework-free and side-effect-free (no DOM, no google.maps, no React), the
 * sibling of `approachZoom` in follow-camera.ts. The integration layer
 * (useFollowCamera) feeds the applied follow zoom in and drives the live map's
 * tilt toward `approachTilt(zoom)` using `stepTilt` on a requestAnimationFrame
 * loop — Google Maps JS has NO built-in animated tilt (setTilt/moveCamera jump
 * instantly), so the easing lives here.
 *
 * Why the tilt is capped where it is (ADR-0033 §2): the car is a billboarded
 * sprite rendered from a fixed camera `CAMERA_ELEVATION_DEG = 57.5` above
 * horizontal (tools/sprite-pipeline/render_car_sprites.py). Google `tilt` is
 * measured from straight-down, so the viewer's ground-elevation angle is
 * `90 - tilt`. At tilt = 90 - 57.5 = 32.5 the viewer sees the ground from
 * exactly the sprite's render angle, so the billboard reads as part of the
 * tilted scene instead of floating. This couples MAX_TILT_DEG to the sprite:
 * if the sprites are ever re-rendered at a different elevation, change it here.
 */

/** Elevation (deg above horizontal) the car sprites were rendered at. */
export const SPRITE_CAMERA_ELEVATION_DEG = 57.5

/** Maximum camera tilt (deg), matched to the sprite so the car stays coherent. */
export const MAX_TILT_DEG = 90 - SPRITE_CAMERA_ELEVATION_DEG // 32.5

// --- Approach-tilt ramp (keyed to the follow zoom approachZoom emits) -----------

/** At/below this zoom the view is flat — far out there are no 3D buildings. */
const TILT_RAMP_LOW_ZOOM = 14
/** At/above this zoom the tilt is full — close in, buildings are dense. */
const TILT_RAMP_HIGH_ZOOM = 16

/**
 * Tilt (deg) for a given follow zoom. Monotonically non-decreasing with zoom so
 * the map "leans in" as the car approaches and the wide far view stays clean:
 *
 * - `zoom <= 14` -> 0 (flat)
 * - `zoom >= 16` -> MAX_TILT_DEG (32.5)
 * - in between: linear interpolation, clamped to [0, MAX_TILT_DEG].
 * - non-finite input -> 0 (safe, flat fallback).
 *
 * Derived from zoom rather than distance so tilt and the applied zoom can never
 * disagree within a render (the follow camera sets both from the same tick).
 */
export function approachTilt(zoom: number): number {
  if (!Number.isFinite(zoom)) return 0
  if (zoom <= TILT_RAMP_LOW_ZOOM) return 0
  if (zoom >= TILT_RAMP_HIGH_ZOOM) return MAX_TILT_DEG

  const t = (zoom - TILT_RAMP_LOW_ZOOM) / (TILT_RAMP_HIGH_ZOOM - TILT_RAMP_LOW_ZOOM)
  return t * MAX_TILT_DEG
}

// --- Frame-rate-independent tilt easing -----------------------------------------

/** Exponential-smoothing time constant (ms). ~3·tau ≈ visually settled. */
const TILT_SMOOTHING_TAU_MS = 250
/** Within this gap, snap to target so the asymptote reaches it exactly. */
const TILT_SNAP_EPSILON_DEG = 0.05

/**
 * Eases `currentDeg` toward `targetDeg` over `dtMs`, frame-rate independent:
 * `next = target - (target - current) · e^(-dt/τ)`. Composing two dt/2 steps
 * equals one dt step exactly, so the lean looks the same at 30 or 120 fps.
 *
 * - never overshoots (asymptotic approach),
 * - snaps to the target once within {@link TILT_SNAP_EPSILON_DEG} so it lands
 *   exactly instead of crawling,
 * - `dtMs <= 0` is a no-op (returns current),
 * - non-finite inputs are treated as 0 / held.
 */
export function stepTilt(currentDeg: number, targetDeg: number, dtMs: number): number {
  const current = Number.isFinite(currentDeg) ? currentDeg : 0
  const target = Number.isFinite(targetDeg) ? targetDeg : 0
  if (dtMs <= 0) return current

  const delta = target - current
  if (Math.abs(delta) <= TILT_SNAP_EPSILON_DEG) return target

  return target - delta * Math.exp(-dtMs / TILT_SMOOTHING_TAU_MS)
}
